import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { useLanguage } from "../contexts/LanguageContext";
import { useOrganizationScope } from "../contexts/OrganizationScopeContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Bell,
  Search,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  Calendar,
  Building2,
  Mail,
  FileText,
  Plane,
  User,
  Battery,
  ShieldAlert,
  Play,
} from "lucide-react";
import { toast } from "sonner";

export const AlertHistory = () => {
  const { t } = useLanguage();
  const {
    activeOrganizationId,
    clearScope,
    isPlatformAdmin,
    organizations,
    setActiveOrganization,
  } = useOrganizationScope();
  const [alerts, setAlerts] = useState([]);
  const [alertTypes, setAlertTypes] = useState([]);
  const [stats, setStats] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [runningCheck, setRunningCheck] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    alertType: "",
    severity: "",
    deliveryStatus: "",
    search: "",
  });
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const fetchAlerts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append("start_date", filters.startDate);
      if (filters.endDate) params.append("end_date", filters.endDate);
      if (activeOrganizationId) {
        params.append("organization_id", activeOrganizationId);
      }
      if (filters.alertType && filters.alertType !== "__all__") {
        params.append("alert_type", filters.alertType);
      }
      if (filters.severity && filters.severity !== "__all__") {
        params.append("severity", filters.severity);
      }
      if (filters.deliveryStatus && filters.deliveryStatus !== "__all__") {
        params.append("delivery_status", filters.deliveryStatus);
      }
      if (filters.search) params.append("search", filters.search);
      params.append("limit", limit);
      params.append("offset", offset);

      const [alertsRes, countRes] = await Promise.all([
        api.get(`/alerts/history?${params}`),
        api.get(`/alerts/history/count?${params}`),
      ]);

      setAlerts(alertsRes.data);
      setTotalCount(countRes.data.count);
    } catch (err) {
      toast.error(t.alerts?.loadError || "Failed to load alert history");
    }
  }, [activeOrganizationId, filters, offset, t]);

  const fetchMetadata = useCallback(async () => {
    try {
      const [typesRes, statsRes, schedulerRes] = await Promise.all([
        api.get('/alerts/types'),
        api.get('/alerts/stats'),
        isPlatformAdmin
          ? api.get('/alerts/scheduler/status')
          : Promise.resolve({ data: null }),
      ]);

      setAlertTypes(typesRes.data);
      setStats(statsRes.data);
      setSchedulerStatus(schedulerRes.data);

    } catch (err) {
      // Silent fail - metadata stays empty
    }
  }, [isPlatformAdmin]);

  useEffect(() => {
    setOffset(0);
  }, [activeOrganizationId]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAlerts(), fetchMetadata()]);
      setLoading(false);
    };
    loadData();
  }, [fetchAlerts, fetchMetadata]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setOffset(0);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchAlerts();
  };

  const handleRunCheck = async () => {
    setRunningCheck(true);
    try {
      const response = await api.post('/alerts/run-check', {});
      toast.success((t.alerts?.checkComplete || "Compliance check completed. {count} alerts processed.").replace("{count}", response.data.alerts_processed));
      await fetchAlerts();
      await fetchMetadata();
    } catch (err) {
      toast.error(t.alerts?.runCheckError || "Failed to run compliance check");
    } finally {
      setRunningCheck(false);
    }
  };

  const getSeverityBadge = (severity) => {
    const variants = {
      critical: "bg-red-100 text-red-700 border-red-200",
      warning: "bg-amber-100 text-amber-700 border-amber-200",
      info: "bg-blue-100 text-blue-700 border-blue-200",
    };
    return (
      <Badge className={`${variants[severity] || variants.info} border`}>
        {severity?.toUpperCase()}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      sent: { class: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle },
      failed: { class: "bg-red-100 text-red-700 border-red-200", icon: XCircle },
      dry_run: { class: "bg-purple-100 text-purple-700 border-purple-200", icon: Clock },
      skipped: { class: "bg-slate-100 text-slate-600 border-slate-200", icon: AlertTriangle },
    };
    const cfg = config[status] || config.skipped;
    const Icon = cfg.icon;
    return (
      <Badge className={`${cfg.class} border flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status?.replace("_", " ").toUpperCase()}
      </Badge>
    );
  };

  const getEntityIcon = (entityType) => {
    const icons = {
      pilot: User,
      aircraft: Plane,
      document: FileText,
      battery: Battery,
      incident: AlertTriangle,
      compliance: ShieldAlert,
    };
    const Icon = icons[entityType] || Bell;
    return <Icon className="h-4 w-4 text-slate-500" />;
  };

  const formatAlertType = (type) => {
    return type
      ?.split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  return (
    <div className="p-8 space-y-6" data-testid="alert-history-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t.alerts?.history || "Alert History"}</h1>
          <p className="text-slate-500 mt-1">
            {t.alerts?.historyDescription || "View and track all compliance alerts sent by the system"}
          </p>
        </div>
        {isPlatformAdmin && (
          <Button
            onClick={handleRunCheck}
            disabled={runningCheck}
            className="flex items-center gap-2"
            data-testid="run-check-btn"
          >
            {runningCheck ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            {t.alerts?.runCheck || "Run Compliance Check"}
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{t.alerts?.totalAlerts || "Total Alerts"}</p>
                  <p className="text-2xl font-bold">{stats.total_alerts}</p>
                </div>
                <Bell className="h-8 w-8 text-slate-300" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{t.alerts?.sent || "Sent"}</p>
                  <p className="text-2xl font-bold text-emerald-600">{stats.sent_count}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{t.alerts?.dryRun || "Dry Run"}</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.dry_run_count}</p>
                </div>
                <Clock className="h-8 w-8 text-purple-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{t.alerts?.failed || "Failed"}</p>
                  <p className="text-2xl font-bold text-red-600">{stats.failed_count}</p>
                </div>
                <XCircle className="h-8 w-8 text-red-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{t.alerts?.skipped || "Skipped"}</p>
                  <p className="text-2xl font-bold text-slate-600">{stats.skipped_count}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-slate-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Scheduler Status */}
      {schedulerStatus && (
        <Card className="bg-slate-50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className={`h-2 w-2 rounded-full ${schedulerStatus.scheduler_active ? "bg-emerald-500" : "bg-red-500"}`} />
                  <span className="text-sm font-medium">
                    {t.alerts?.scheduler || "Scheduler"}: {schedulerStatus.scheduler_active ? "Active" : "Inactive"}
                  </span>
                </div>
                <span className="text-sm text-slate-500">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  {schedulerStatus.schedule}
                </span>
                <Badge variant={schedulerStatus.email_mode === "real" ? "success" : "secondary"}>
                  <Mail className="h-3 w-3 mr-1" />
                  {schedulerStatus.email_mode === "real" ? "Real Emails" : "Dry Run Mode"}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            {t.common?.filters || "Filters"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <div>
              <Label className="text-xs">{t.alerts?.startDate || "Start Date"}</Label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange("startDate", e.target.value)}
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs">{t.alerts?.endDate || "End Date"}</Label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange("endDate", e.target.value)}
                className="h-9"
              />
            </div>
            {isPlatformAdmin && (
              <div>
                <Label className="text-xs">{t.common?.organization || "Organization"}</Label>
                <Select
                  value={activeOrganizationId || "__all__"}
                  onValueChange={(value) => value === "__all__" ? clearScope() : setActiveOrganization(value)}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder={t.common?.all || "All"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">{t.common?.all || "All"}</SelectItem>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs">{t.alerts?.alertType || "Alert Type"}</Label>
              <Select
                value={filters.alertType || "__all__"}
                onValueChange={(v) => handleFilterChange("alertType", v)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder={t.common?.all || "All"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t.common?.all || "All"}</SelectItem>
                  {alertTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t.alerts?.severity || "Severity"}</Label>
              <Select
                value={filters.severity || "__all__"}
                onValueChange={(v) => handleFilterChange("severity", v)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder={t.common?.all || "All"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t.common?.all || "All"}</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t.alerts?.status || "Status"}</Label>
              <Select
                value={filters.deliveryStatus || "__all__"}
                onValueChange={(v) => handleFilterChange("deliveryStatus", v)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder={t.common?.all || "All"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t.common?.all || "All"}</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="dry_run">Dry Run</SelectItem>
                  <SelectItem value="skipped">Skipped</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t.common?.search || "Search"}</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder={t.alerts?.searchPlaceholder || "Search..."}
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  className="h-9 pl-8"
                />
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Alerts Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <CardTitle className="text-base">
              {t.alerts?.alertLog || "Alert Log"} ({totalCount})
            </CardTitle>
            <Button variant="outline" size="sm" onClick={fetchAlerts}>
              <RefreshCw className="h-4 w-4 mr-1" />
              {t.common?.refresh || "Refresh"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Bell className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>{t.alerts?.noAlerts || "No alerts found"}</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[140px]">{t.alerts?.timestamp || "Timestamp"}</TableHead>
                      <TableHead>{t.alerts?.alertType || "Alert Type"}</TableHead>
                      <TableHead>{t.alerts?.severity || "Severity"}</TableHead>
                      <TableHead>{t.common?.organization || "Organization"}</TableHead>
                      <TableHead>{t.alerts?.entity || "Entity"}</TableHead>
                      <TableHead>{t.alerts?.recipient || "Recipient"}</TableHead>
                      <TableHead>{t.alerts?.status || "Status"}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {alerts.map((alert) => (
                      <TableRow key={alert.id} data-testid={`alert-row-${alert.id}`}>
                        <TableCell className="text-xs text-slate-500">
                          {formatDate(alert.created_at)}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm font-medium">
                            {formatAlertType(alert.alert_type)}
                          </span>
                        </TableCell>
                        <TableCell>{getSeverityBadge(alert.severity)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-slate-400" />
                            <span className="text-sm">{alert.organization_name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getEntityIcon(alert.entity_type)}
                            <span className="text-sm">{alert.entity_name || "-"}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-slate-400" />
                            <span className="text-sm text-slate-600">{alert.recipient_email}</span>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(alert.delivery_status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalCount > limit && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <span className="text-sm text-slate-500">
                    {t.common?.showing || "Showing"} {offset + 1}-{Math.min(offset + limit, totalCount)} {t.common?.of || "of"} {totalCount}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={offset === 0}
                      onClick={() => setOffset(Math.max(0, offset - limit))}
                    >
                      {t.common?.previous || "Previous"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={offset + limit >= totalCount}
                      onClick={() => setOffset(offset + limit)}
                    >
                      {t.common?.next || "Next"}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AlertHistory;
